import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { DatePicker, Input, Select, Spin } from 'antd'
import dayjs from 'dayjs'
import {
	ChevronLeft,
	ChevronRight,
	Plus,
	RotateCcw,
	Search,
	SlidersHorizontal,
	TriangleAlert,
} from 'lucide-react'
import DemoShell from './components/DemoShell'
import AsyncSelect, { OptionRow } from './components/AsyncSelect'
import { Card, Field } from './components/Field'
import { EmptyRow, TableWrap, Td, Th } from './components/Table'
import { InvoiceStatusBadge, SaleTypeBadge } from './components/Badges'
import { getBusinessPartners, getInvoices } from './api'
import { useSalesEmployees, useWarehouses } from './hooks'
import {
	CANCELED_OPTIONS,
	DATE_FORMAT,
	DEFAULT_PAGE_SIZE,
	DEMO_INVOICE_NEW,
	DOC_STATUS_OPTIONS,
	IMEI_FALLBACK_PAGE_SIZE,
	PAGE_SIZE_OPTIONS,
	SALE_TYPE_OPTIONS,
	demoInvoicePath,
} from './constants'
import {
	formatDate,
	formatMoney,
	getApiErrorMessage,
	invoiceImeis,
	invoiceItemNames,
	invoiceWarehouses,
	isCanceled,
	isUnauthorized,
	matchesImei,
} from './utils'

const TABLE_COLSPAN = 11

const FILTER_KEYS = [
	'cardCode',
	'cardName',
	'customerPhone',
	'docNum',
	'imei',
	'slpCode',
	'saleType',
	'whsCode',
	'docStatus',
	'canceled',
	'startDate',
	'endDate',
]

// Eskisidagi «Продажи» ekrani kabi: yil boshidan bugungacha
const DEFAULT_FILTERS = FILTER_KEYS.reduce(
	(acc, key) => ({ ...acc, [key]: '' }),
	{
		canceled: 'N',
		startDate: dayjs().startOf('year').format(DATE_FORMAT),
		endDate: dayjs().format(DATE_FORMAT),
	},
)

// Query bo'sh bo'lsa — defaultlar, aks holda faqat URL dagi qiymatlar
// (foydalanuvchi filtrni tozalasa u defaultdan qaytib chiqmasligi uchun).
const readFilters = (searchParams) => {
	const pristine = searchParams.toString() === ''
	return FILTER_KEYS.reduce((acc, key) => {
		acc[key] = searchParams.get(key) ?? (pristine ? DEFAULT_FILTERS[key] : '')
		return acc
	}, {})
}

const toDayjs = (value) => (value ? dayjs(value, DATE_FORMAT) : null)

const joinLines = (values) => (values.length ? values.join(', ') : '—')

const DemoInvoices = () => {
	const navigate = useNavigate()
	const [searchParams, setSearchParams] = useSearchParams()

	const { data: warehouses } = useWarehouses()
	const { data: salesEmployees } = useSalesEmployees()

	// TZ 7: filtrlar URL query ga yoziladi — sahifa yangilanganda yo'qolmaydi
	const applied = useMemo(() => readFilters(searchParams), [searchParams])
	const page = Number(searchParams.get('page') || 0)
	const pageSize = Number(searchParams.get('pageSize') || DEFAULT_PAGE_SIZE)

	const [draft, setDraft] = useState(applied)
	const [showMore, setShowMore] = useState(false)
	const [rows, setRows] = useState([])
	const [fetchedCount, setFetchedCount] = useState(0)
	const [imeiFallback, setImeiFallback] = useState(false)
	const [loading, setLoading] = useState(true)
	const [error, setError] = useState('')
	const [customerOption, setCustomerOption] = useState(null)

	const queryKey = searchParams.toString()

	useEffect(() => {
		setDraft(readFilters(searchParams))
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [queryKey])

	useEffect(() => {
		const controller = new AbortController()
		const config = { signal: controller.signal }
		setLoading(true)
		setError('')
		setImeiFallback(false)

		const params = { ...applied, pageSize, skip: page }

		// Backendda `imei` filtri hozircha 500 qaytaradi — shu sababli so'rov
		// IMEI siz takrorlanadi va sahifa mijoz tomonda filtrlanadi.
		let usedFallback = false
		const load = () =>
			getInvoices(params, config).catch((err) => {
				if (!applied.imei || controller.signal.aborted || isUnauthorized(err))
					throw err
				usedFallback = true
				const { imei, ...rest } = params
				return getInvoices(
					{ ...rest, pageSize: IMEI_FALLBACK_PAGE_SIZE },
					config,
				).then((list) => {
					setImeiFallback(true)
					setFetchedCount(list.length)
					return list.filter((invoice) => matchesImei(invoice, imei))
				})
			})

		load()
			.then((list) => {
				setRows(list)
				if (!usedFallback) setFetchedCount(list.length)
			})
			.catch((err) => {
				if (controller.signal.aborted) return
				if (isUnauthorized(err)) {
					navigate('/login', { replace: true })
					return
				}
				setRows([])
				setFetchedCount(0)
				setError(getApiErrorMessage(err))
			})
			.finally(() => {
				if (!controller.signal.aborted) setLoading(false)
			})

		return () => controller.abort()
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [queryKey])

	const pushParams = useCallback(
		(next) => {
			const params = new URLSearchParams()
			FILTER_KEYS.forEach((key) => {
				const value = next[key]
				if (value !== '' && value !== undefined && value !== null)
					params.set(key, value)
			})
			// `page` doim yoziladi — query bo'sh qolmaydi, shuning uchun
			// tozalangan filtr defaultdan qaytib chiqmaydi.
			params.set('page', String(next.page || 0))
			if (next.pageSize && next.pageSize !== DEFAULT_PAGE_SIZE)
				params.set('pageSize', String(next.pageSize))
			setSearchParams(params)
		},
		[setSearchParams],
	)

	const applyFilters = () => pushParams({ ...draft, page: 0, pageSize })

	const resetFilters = () => {
		setCustomerOption(null)
		setDraft(DEFAULT_FILTERS)
		pushParams({ ...DEFAULT_FILTERS, page: 0, pageSize: DEFAULT_PAGE_SIZE })
	}

	const goToPage = (nextPage) =>
		pushParams({ ...applied, page: Math.max(0, nextPage), pageSize })

	const setField = (key) => (value) =>
		setDraft((prev) => ({ ...prev, [key]: value ?? '' }))

	const activeFilterCount = FILTER_KEYS.filter(
		(key) => applied[key] !== '' && applied[key] !== DEFAULT_FILTERS[key],
	).length

	const hasNextPage = imeiFallback
		? fetchedCount === IMEI_FALLBACK_PAGE_SIZE
		: fetchedCount === pageSize

	return (
		<DemoShell
			title="Hujjatlar"
			subtitle="A/R Invoices — SAP dan to‘g‘ridan-to‘g‘ri, keshsiz"
			actions={
				<button
					type="button"
					onClick={() => navigate(DEMO_INVOICE_NEW)}
					className="inline-flex h-9 items-center gap-2 rounded-xl bg-indigo-600 px-4 text-sm font-medium text-white shadow-sm transition hover:bg-indigo-700"
				>
					<Plus size={16} /> Yangi hujjat
				</button>
			}
		>
			<Card className="mb-5">
				{/* Eskisidagi kabi ochiq holat: Mijoz · IMEI · Sana (dan / gacha) */}
				<div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
					<Field label="Mijoz">
						<AsyncSelect
							value={draft.cardCode || undefined}
							placeholder="Ism, kod yoki telefon"
							fetcher={(search) => getBusinessPartners({ search })}
							extraOptions={customerOption ? [customerOption] : []}
							mapOption={(bp) => ({
								value: bp.cardCode,
								title: bp.cardName,
								label: (
									<OptionRow
										title={bp.cardName}
										subtitle={[bp.phone, bp.cardCode]
											.filter(Boolean)
											.join(' · ')}
									/>
								),
							})}
							onChange={(value, option) => {
								setCustomerOption(value ? option : null)
								setField('cardCode')(value)
							}}
						/>
					</Field>

					<Field label="IMEI">
						<Input
							value={draft.imei}
							onChange={(e) => setField('imei')(e.target.value)}
							onPressEnter={applyFilters}
							placeholder="Sotilgan tovar seriyasi"
						/>
					</Field>

					<Field label="Sana (dan)">
						<DatePicker
							className="w-full"
							format={DATE_FORMAT}
							value={toDayjs(draft.startDate)}
							maxDate={toDayjs(draft.endDate) || undefined}
							onChange={(date) =>
								setField('startDate')(date?.format(DATE_FORMAT) || '')
							}
						/>
					</Field>

					<Field label="Sana (gacha)">
						<DatePicker
							className="w-full"
							format={DATE_FORMAT}
							value={toDayjs(draft.endDate)}
							minDate={toDayjs(draft.startDate) || undefined}
							onChange={(date) =>
								setField('endDate')(date?.format(DATE_FORMAT) || '')
							}
						/>
					</Field>
				</div>

				{showMore && (
					<div className="mt-3 grid grid-cols-1 gap-3 border-t border-slate-100 pt-4 sm:grid-cols-2 lg:grid-cols-4">
						<Field label="Telefon">
							<Input
								value={draft.customerPhone}
								onChange={(e) => setField('customerPhone')(e.target.value)}
								onPressEnter={applyFilters}
								placeholder="998901234567"
							/>
						</Field>

						<Field label="Hujjat raqami">
							<Input
								value={draft.docNum}
								onChange={(e) =>
									setField('docNum')(e.target.value.replace(/\D/g, ''))
								}
								onPressEnter={applyFilters}
								placeholder="24766"
							/>
						</Field>

						<Field label="Mijoz ismi">
							<Input
								value={draft.cardName}
								onChange={(e) => setField('cardName')(e.target.value)}
								onPressEnter={applyFilters}
								placeholder="Ism bo‘yicha"
							/>
						</Field>

						<Field label="Sotuvchi">
							<Select
								className="w-full"
								allowClear
								showSearch
								optionFilterProp="label"
								value={draft.slpCode ? Number(draft.slpCode) : undefined}
								onChange={(value) => setField('slpCode')(value ?? '')}
								placeholder="Hammasi"
								options={salesEmployees.map((emp) => ({
									value: emp.slpCode,
									label: emp.slpName,
								}))}
							/>
						</Field>

						<Field label="Filial (sklad)">
							<Select
								className="w-full"
								allowClear
								showSearch
								optionFilterProp="label"
								value={draft.whsCode || undefined}
								onChange={(value) => setField('whsCode')(value ?? '')}
								placeholder="Hammasi"
								options={warehouses.map((whs) => ({
									value: whs.whsCode,
									label: `${whs.whsCode} · ${whs.whsName}`,
								}))}
							/>
						</Field>

						<Field label="Savdo turi">
							<Select
								className="w-full"
								value={draft.saleType || ''}
								onChange={setField('saleType')}
								options={SALE_TYPE_OPTIONS}
							/>
						</Field>

						<Field label="Holati">
							<Select
								className="w-full"
								value={draft.docStatus || ''}
								onChange={setField('docStatus')}
								options={DOC_STATUS_OPTIONS}
							/>
						</Field>

						<Field label="Bekor qilinganlar">
							<Select
								className="w-full"
								value={draft.canceled}
								onChange={setField('canceled')}
								options={CANCELED_OPTIONS}
							/>
						</Field>

						<Field label="Sahifadagi qatorlar">
							<Select
								className="w-full"
								value={pageSize}
								onChange={(value) =>
									pushParams({ ...applied, page: 0, pageSize: value })
								}
								options={PAGE_SIZE_OPTIONS.map((size) => ({
									value: size,
									label: `${size} ta`,
								}))}
							/>
						</Field>
					</div>
				)}

				<div className="mt-4 flex flex-wrap items-center gap-2">
					<button
						type="button"
						onClick={applyFilters}
						className="inline-flex h-10 items-center gap-2 rounded-xl bg-slate-900 px-4 text-sm font-medium text-white transition hover:bg-slate-800"
					>
						<Search size={16} /> Qidirish
					</button>
					<button
						type="button"
						onClick={resetFilters}
						className="inline-flex h-10 items-center gap-2 rounded-xl border border-slate-200 px-4 text-sm font-medium text-slate-600 transition hover:border-slate-300"
					>
						<RotateCcw size={15} /> Tozalash
					</button>
					<button
						type="button"
						onClick={() => setShowMore((prev) => !prev)}
						className="inline-flex h-10 items-center gap-2 rounded-xl border border-slate-200 px-4 text-sm font-medium text-slate-600 transition hover:border-slate-300"
					>
						<SlidersHorizontal size={15} />
						{showMore ? 'Filtrlarni yig‘ish' : 'Qo‘shimcha filtrlar'}
						{activeFilterCount > 0 && (
							<span className="rounded-full bg-indigo-50 px-2 text-[11px] font-semibold text-indigo-600">
								{activeFilterCount}
							</span>
						)}
					</button>
				</div>
			</Card>

			{error && (
				<div className="mb-4 flex items-start gap-3 rounded-2xl border border-rose-200 bg-rose-50 p-4 text-sm text-rose-700">
					<TriangleAlert size={18} className="mt-0.5 shrink-0" />
					<p className="font-medium">{error}</p>
				</div>
			)}

			{imeiFallback && (
				<div className="mb-4 flex items-start gap-3 rounded-2xl border border-amber-200 bg-amber-50 p-4 text-[13px] text-amber-700">
					<TriangleAlert size={18} className="mt-0.5 shrink-0" />
					<div>
						<p className="font-medium">
							IMEI filtri backendda xato qaytardi — qidiruv mijoz tomonda
							bajarildi.
						</p>
						<p className="mt-1 text-amber-600/80">
							{`Shu sahifadagi ${fetchedCount} ta hujjat tekshirildi. Aniqroq natija uchun sana oralig‘ini toraytiring.`}
						</p>
					</div>
				</div>
			)}

			<Spin spinning={loading}>
				{/* Ustunlar eskisidagi «Продажи» jadvali kabi: sana · mijoz · tovar ·
				    IMEI · savdo turi · summa · filial */}
				<TableWrap minWidth="min-w-[1320px]">
					<thead>
						<tr>
							<Th>№</Th>
							<Th>Sana</Th>
							<Th>Mijoz</Th>
							<Th>Tovar</Th>
							<Th>IMEI</Th>
							<Th>Savdo turi</Th>
							<Th align="right">Summasi</Th>
							<Th align="right">To‘langan</Th>
							<Th align="right">Qolgan qarz</Th>
							<Th>Filial</Th>
							<Th>Holati</Th>
						</tr>
					</thead>
					<tbody>
						{!loading && rows.length === 0 && (
							<EmptyRow
								colSpan={TABLE_COLSPAN}
								text="Filtrga mos hujjat topilmadi"
							/>
						)}
						{rows.map((invoice) => {
							const canceled = isCanceled(invoice)
							const items = joinLines(invoiceItemNames(invoice))
							const imeis = joinLines(invoiceImeis(invoice))
							return (
								<tr
									key={invoice.docEntry}
									onClick={() => navigate(demoInvoicePath(invoice.docEntry))}
									className={`cursor-pointer transition hover:bg-indigo-50/40 ${
										canceled ? 'text-slate-400' : ''
									}`}
								>
									<Td className={canceled ? 'line-through' : ''}>
										<span className="font-semibold text-slate-900">
											{invoice.docNum}
										</span>
									</Td>
									<Td className="whitespace-nowrap">
										{formatDate(invoice.docDate)}
									</Td>
									<Td>
										<div className="max-w-[220px]">
											<p className="truncate font-medium text-slate-800">
												{invoice.cardName || '—'}
											</p>
											<p className="truncate text-[12px] text-slate-400">
												{invoice.customerPhone || invoice.cardCode || '—'}
											</p>
										</div>
									</Td>
									<Td>
										<p
											className="max-w-[240px] truncate text-slate-700"
											title={items}
										>
											{items}
										</p>
									</Td>
									<Td>
										<p
											className="max-w-[160px] truncate font-mono text-[13px] text-slate-600"
											title={imeis}
										>
											{imeis}
										</p>
									</Td>
									<Td>
										<SaleTypeBadge
											saleType={invoice.saleType}
											installments={invoice.installments}
										/>
									</Td>
									<Td className="whitespace-nowrap text-right font-medium">
										{formatMoney(invoice.docTotal)}
									</Td>
									<Td className="whitespace-nowrap text-right text-emerald-600">
										{formatMoney(invoice.paidToDate)}
									</Td>
									<Td className="whitespace-nowrap text-right font-medium text-slate-800">
										{formatMoney(invoice.openBalance)}
									</Td>
									<Td className="whitespace-nowrap text-slate-600">
										{joinLines(invoiceWarehouses(invoice))}
									</Td>
									<Td>
										<InvoiceStatusBadge invoice={invoice} />
									</Td>
								</tr>
							)
						})}
					</tbody>
				</TableWrap>
			</Spin>

			{/* TZ 1.2: totalCount qaytmaydi — «Oldingi / Keyingi» sahifalash */}
			<div className="mt-4 flex items-center justify-between gap-3">
				<p className="text-[12px] text-slate-400">
					{`Sahifa ${page + 1} · ${rows.length} ta qator`}
				</p>
				<div className="flex items-center gap-2">
					<button
						type="button"
						disabled={page === 0 || loading}
						onClick={() => goToPage(page - 1)}
						className="inline-flex h-9 items-center gap-1 rounded-xl border border-slate-200 px-3 text-sm text-slate-600 transition enabled:hover:border-slate-300 disabled:opacity-40"
					>
						<ChevronLeft size={16} /> Oldingi
					</button>
					<button
						type="button"
						disabled={!hasNextPage || loading}
						onClick={() => goToPage(page + 1)}
						className="inline-flex h-9 items-center gap-1 rounded-xl border border-slate-200 px-3 text-sm text-slate-600 transition enabled:hover:border-slate-300 disabled:opacity-40"
					>
						Keyingi <ChevronRight size={16} />
					</button>
				</div>
			</div>
		</DemoShell>
	)
}

export default DemoInvoices
